import "primeicons/primeicons.css";
import { Button as PrimeButton } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import "primereact/resources/primereact.min.css";
import "primereact/resources/themes/saga-blue/theme.css";
import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { RoomType } from "../../models/roomType.model";
import { roomTypeService } from "../../service/roomType.service";
import PageHeader from "../PageHeader";

const initialForm: Partial<RoomType> = {
  id: 0,
  name: "",
  description: "",
};

const RoomTypeComponent: React.FC = () => {
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<Partial<RoomType>>(initialForm);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(() => {
    const savedFirst = localStorage.getItem("roomTypeTableFirst");
    const savedRows = localStorage.getItem("roomTypeTableRows");
    return savedFirst && savedRows
      ? Math.floor(parseInt(savedFirst) / parseInt(savedRows))
      : 0;
  });
  const [rows, setRows] = useState<number>(() => {
    const savedRows = localStorage.getItem("roomTypeTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });

  const fetchRoomTypes = async () => {
    try {
      const data = await roomTypeService.getAll();
      setRoomTypes(data);
    } catch (error) {
      console.error("Error fetching room types:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch room types",
        icon: "error",
      });
    }
  };

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (!form.name?.trim()) errs.name = "Name is required";
    return errs;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      if (form.id) {
        const updated = await roomTypeService.update(form.id, form);
        setRoomTypes((prev) =>
          prev.map((t) => (t.id === form.id ? updated : t))
        );
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Room type updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "swal2-toast-bottom" },
        });
      } else {
        const created = await roomTypeService.create(form);
        setRoomTypes((prev) => [...prev, created]);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Room type created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "swal2-toast-bottom" },
        });
      }
      setShowModal(false);
      setForm(initialForm);
      setSuccess(true);
    } catch (error: any) {
      setError(
        error?.response?.data?.message ||
          error.message ||
          `Failed to ${form.id ? "update" : "create"} room type`
      );
      console.error("Error saving room type:", error);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title:
          error?.response?.data?.message ||
          error.message ||
          `Failed to ${form.id ? "update" : "create"} room type`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (roomType: RoomType) => {
    setForm(roomType);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await roomTypeService.delete(id);
      // Update the local state immediately
      setRoomTypes((prev) => prev.filter((t) => t.id !== id));
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Room type deleted successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } catch (error) {
      console.error("Error deleting room type:", error);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to delete room type.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    }
  };

  const actionTemplate = (rowData: RoomType) => {
    return (
      <div className="d-flex align-items-center gap-2">
        <Button
          variant="link"
          className="p-0"
          onClick={() => handleEdit(rowData)}
          title="Edit"
          style={{ color: "#198754" }}
        >
          <Icon.PencilSquare size={20} />
        </Button>
        <Button
          variant="link"
          className="p-0"
          onClick={() => handleDelete(rowData.id)}
          title="Delete"
          style={{ color: "#dc3545" }}
        >
          <Icon.Trash size={20} />
        </Button>
      </div>
    );
  };

  const handleRefresh = () => fetchRoomTypes();

  return (
    <div className="content">
      <PageHeader
        title="Room Types"
        beforeTitle={
          <PrimeButton
            icon="pi pi-refresh"
            rounded
            text
            severity="secondary"
            onClick={handleRefresh}
            tooltip="Refresh"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Refresh Room Types"
          />
        }
      />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center gap-2 my-3">
                <IconField iconPosition="left" className="flex-grow-1">
                  <InputIcon>
                    <Icon.Search />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setCurrentPage(0);
                      localStorage.setItem("roomTypeTableFirst", "0");
                    }}
                    placeholder="Search"
                    style={{ minWidth: 250 }}
                  />
                </IconField>
                <Button
                  className="btn btn-primary ms-2"
                  style={{ minWidth: 160 }}
                  onClick={() => {
                    setForm(initialForm);
                    setShowModal(true);
                  }}
                >
                  <Icon.PlusCircle className="me-1" /> Create Room Types
                </Button>
              </div>
              <DataTable
                value={roomTypes}
                globalFilter={globalFilter}
                emptyMessage="No room types found"
                paginator
                rows={rows}
                first={currentPage * rows}
                rowsPerPageOptions={[5, 10, 20]}
                onPage={(e) => {
                  const newPage = Math.floor(e.first / e.rows);
                  setCurrentPage(newPage);
                  setRows(e.rows);
                  localStorage.setItem(
                    "roomTypeTableFirst",
                    (newPage * e.rows).toString()
                  );
                  localStorage.setItem("roomTypeTableRows", e.rows.toString());
                }}
                stripedRows
                className="grid-table"
              >
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                <Column
                  header="Actions"
                  body={actionTemplate}
                  style={{ width: "150px" }}
                />
              </DataTable>
            </div>
          </div>
        </div>
      </div>

      <Modal
        size="lg"
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{form.id ? "Update" : "Create"} Room Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2">
            <div className="col-12">
              <Form.Label>
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="name"
                value={form.name || ""}
                onChange={handleChange}
                isInvalid={!!errors.name}
              />
              {errors.name && (
                <div className="text-danger tx-13">{errors.name}</div>
              )}
            </div>

            <div className="col-12">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={form.description || ""}
                onChange={handleChange}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RoomTypeComponent;
